import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';

const WEEKDAY_LABELS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function CourseBookingsOverview() {
  const { authFetch } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [search, setSearch] = useState('');
  const [resendingId, setResendingId] = useState(null);

  const loadBookings = () => {
    setLoading(true);
    authFetch('/api/course-bookings')
      .then(res => res.json())
      .then(data => {
        setBookings(data.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Fehler beim Laden der Anmeldungen');
        setLoading(false);
      });
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const courses = useMemo(() => {
    const map = new Map();
    bookings.forEach(b => {
      if (b.course_id && !map.has(b.course_id)) {
        map.set(b.course_id, { id: b.course_id, name: b.course_name, color: b.course_color });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [bookings]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return bookings.filter(b => {
      if (courseFilter && String(b.course_id) !== String(courseFilter)) return false;
      if (term) {
        const haystack = `${b.first_name || ''} ${b.last_name || ''} ${b.email || ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [bookings, courseFilter, search]);

  const cancelBooking = (id) => {
    if (!window.confirm('Diese Anmeldung wirklich stornieren?')) return;
    authFetch(`/api/course-bookings/${id}`, { method: 'DELETE' })
      .then(res => { if (!res.ok) throw new Error(); })
      .then(loadBookings)
      .catch(() => alert('Fehler beim Stornieren.'));
  };

  const resendConfirmation = (id) => {
    setResendingId(id);
    authFetch(`/api/course-bookings/${id}/resend-confirmation`, { method: 'POST' })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.error || 'Fehler');
        alert('Bestätigungs-E-Mail wurde erneut gesendet.');
      })
      .catch(err => alert('Fehler beim Senden: ' + err.message))
      .finally(() => setResendingId(null));
  };

  if (loading) return <p>Lade Anmeldungen...</p>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title">Anmeldungen ({filtered.length}{filtered.length !== bookings.length ? ` von ${bookings.length}` : ''})</h5>
        <p className="text-muted small">
          Übersicht aller Anmeldungen zu wöchentlichen Kursen – über alle Kurse und Termine hinweg, neueste zuerst.
        </p>

        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <select className="form-select form-select-sm" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
              <option value="">Alle Kurse</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Suche nach Name oder E-Mail..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="text-muted">Keine Anmeldungen gefunden.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th>Angemeldet am</th>
                  <th>Kurs</th>
                  <th>Termin</th>
                  <th>Mitglied</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => (
                  <tr key={b.id}>
                    <td className="small">{formatDateTime(b.created_at)}</td>
                    <td>
                      <span className="badge me-1" style={{ backgroundColor: b.course_color || '#6c757d' }}>&nbsp;</span>
                      {b.course_name || '-'}
                    </td>
                    <td className="small">
                      {b.weekday !== null && b.weekday !== undefined ? `${WEEKDAY_LABELS[b.weekday]}` : ''}
                      {b.time ? `, ${b.time.slice(0, 5)} Uhr` : ''}
                      <br />
                      {formatDate(b.date)}
                    </td>
                    <td>
                      {b.first_name} {b.last_name}
                      {b.email && (
                        <>
                          <br />
                          <a href={`mailto:${b.email}`} className="small">{b.email}</a>
                        </>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${b.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                        {b.status === 'active' ? 'Aktiv' : b.status}
                      </span>
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-secondary me-1"
                        disabled={!b.email || resendingId === b.id}
                        onClick={() => resendConfirmation(b.id)}
                      >
                        {resendingId === b.id ? '...' : 'E-Mail erneut senden'}
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => cancelBooking(b.id)}>
                        Stornieren
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CourseBookingsOverview;
